/**
 * Stable bridge between React hooks and identity-scoped sync managers.
 *
 * The API identity can be briefly unavailable while the authenticated patient
 * is loading. Captures made during that gap stay in memory, are deduplicated,
 * and do not resolve until the matching/first identity manager has taken
 * durable ownership. Captures associated with a previous identity are never
 * transferred to a different patient.
 */

import {
  PatientSyncManager,
  type PatientSyncSnapshot,
} from "./patientSyncManager";
import {
  countPatientWriteSections,
  patientWriteSection,
  type MedicalHistoryWriteSection,
} from "./patientWriteQueue";

export type PatientSyncGateSnapshot = PatientSyncSnapshot & {
  identityReady: boolean;
};

/**
 * A staged capture could not be delivered: its target identity never
 * re-attached before a different identity took over (or the gate was
 * disposed). The answer is never transferred across identities; awaiting
 * screens receive this rejection instead of hanging forever.
 */
export class PatientSyncStagedWriteError extends Error {
  readonly code = "patient-sync-staged-write-undeliverable";

  constructor(message: string) {
    super(message);
    this.name = "PatientSyncStagedWriteError";
  }
}

type StagedSubmission = {
  body: Record<string, unknown>;
  fingerprint: string;
  targetIdentity: string | null;
  accepted: Promise<void>;
  resolveAccepted: () => void;
  rejectAccepted: (error: Error) => void;
  /** The acceptance promise was already settled as undeliverable. */
  settledUndeliverable: boolean;
  /** Product recovery edit that must atomically replace this exact failure. */
  replaceFailedId?: string;
};

type FailedEditIntent = {
  id: string;
  sectionKey: MedicalHistoryWriteSection;
  targetIdentity: string | null;
};

const EMPTY_GATE_SNAPSHOT: PatientSyncGateSnapshot = {
  hydrated: false,
  pending: 0,
  pendingSections: {},
  failed: [],
  accepting: 0,
  undurable: 0,
  storageError: false,
  inFlight: false,
  identityReady: false,
};

export class PatientSyncGate {
  private manager: PatientSyncManager | null = null;
  private managerIdentity: string | null = null;
  private lastIdentity: string | null = null;
  private unsubscribeManager: (() => void) | null = null;
  private staged: StagedSubmission[] = [];
  private failedEditIntent: FailedEditIntent | null = null;
  private listeners = new Set<(snapshot: PatientSyncGateSnapshot) => void>();
  private currentSnapshot = EMPTY_GATE_SNAPSHOT;

  cachedSnapshot(): PatientSyncGateSnapshot {
    return this.currentSnapshot;
  }

  subscribe(listener: (snapshot: PatientSyncGateSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.currentSnapshot);
    return () => this.listeners.delete(listener);
  }

  attach(manager: PatientSyncManager, identity: string): void {
    if (this.manager === manager && this.managerIdentity === identity) return;
    if (
      this.failedEditIntent?.targetIdentity !== null &&
      this.failedEditIntent?.targetIdentity !== identity
    ) {
      this.failedEditIntent = null;
    }
    this.unsubscribeManager?.();
    this.manager = manager;
    this.managerIdentity = identity;
    this.lastIdentity = identity;
    this.unsubscribeManager = manager.subscribe(() => this.publish());
    this.settleForeignStaged(identity);
    this.publish();
    void this.transferEligible(identity, manager);
  }

  /**
   * A different identity took over, so captures staged for another patient
   * cannot be delivered now (and may never be). Settle their acceptance so
   * awaiting screens do not hang forever, while KEEPING the captured bodies
   * staged: they are never transferred across identities, but they still
   * transfer to their own identity if it ever re-attaches.
   */
  private settleForeignStaged(identity: string): void {
    for (const entry of this.staged) {
      if (
        entry.targetIdentity !== null &&
        entry.targetIdentity !== identity &&
        !entry.settledUndeliverable
      ) {
        entry.settledUndeliverable = true;
        entry.rejectAccepted(
          new PatientSyncStagedWriteError(
            "This answer belongs to a different patient session and was not delivered.",
          ),
        );
      }
    }
  }

  /**
   * Tear the gate down: settle every staged acceptance so no awaiting screen
   * is left hanging. Staged bodies are dropped with the gate — they are never
   * transferred to another gate or identity.
   */
  dispose(): void {
    const staged = this.staged;
    this.staged = [];
    this.unsubscribeManager?.();
    this.unsubscribeManager = null;
    this.manager = null;
    this.managerIdentity = null;
    this.failedEditIntent = null;
    for (const entry of staged) {
      if (entry.settledUndeliverable) continue;
      entry.settledUndeliverable = true;
      entry.rejectAccepted(
        new PatientSyncStagedWriteError(
          "The patient sync gate was disposed before this answer could be delivered.",
        ),
      );
    }
    this.publish();
  }

  detach(manager: PatientSyncManager): void {
    if (this.manager !== manager) return;
    this.unsubscribeManager?.();
    this.unsubscribeManager = null;
    this.manager = null;
    this.managerIdentity = null;
    this.publish();
  }

  enqueue(body: Record<string, unknown>): Promise<void> {
    const serializedBody = JSON.stringify(body);
    const targetIdentity = this.lastIdentity;
    const sectionKey = patientWriteSection(body);
    const failedEdit =
      this.failedEditIntent &&
      this.failedEditIntent.sectionKey === sectionKey &&
      (this.failedEditIntent.targetIdentity === null ||
        this.failedEditIntent.targetIdentity === targetIdentity)
        ? this.failedEditIntent
        : undefined;

    if (this.manager) {
      if (!failedEdit) return this.manager.enqueue(body);
      const manager = this.manager;
      const identity = this.managerIdentity;
      this.failedEditIntent = null;
      return manager.editFailedWrite(failedEdit.id, body).then(
        () => {
          if (
            this.manager === manager &&
            this.managerIdentity === identity &&
            this.currentSnapshot.failed.some(
              (failed) => failed.id === failedEdit.id,
            )
          ) {
            this.failedEditIntent = failedEdit;
          }
        },
        (error: unknown) => {
          if (this.manager === manager && this.managerIdentity === identity) {
            this.failedEditIntent = failedEdit;
          }
          throw error;
        },
      );
    }

    // Share a staged acceptance only with the MOST RECENT capture for the
    // same identity. Matching earlier staged entries would collapse an A-B-A
    // resubmission into the first capture and let the stale intervening
    // answer be delivered last once a manager attaches.
    const latestStaged = this.staged.at(-1);
    const duplicate =
      latestStaged &&
      latestStaged.targetIdentity === targetIdentity &&
      latestStaged.fingerprint === serializedBody &&
      latestStaged.replaceFailedId === failedEdit?.id
        ? latestStaged
        : undefined;
    if (duplicate) return duplicate.accepted;

    if (failedEdit) this.failedEditIntent = null;
    const capturedBody = JSON.parse(serializedBody) as Record<string, unknown>;
    let resolveAccepted = () => {};
    let rejectAccepted: (error: Error) => void = () => {};
    const accepted = new Promise<void>((resolve, reject) => {
      resolveAccepted = resolve;
      rejectAccepted = reject;
    });
    this.staged.push({
      body: capturedBody,
      fingerprint: serializedBody,
      targetIdentity,
      accepted,
      resolveAccepted,
      rejectAccepted,
      settledUndeliverable: false,
      replaceFailedId: failedEdit?.id,
    });
    this.publish();
    return accepted;
  }

  retryFailedWrite(id: string): Promise<void> {
    if (!this.manager) {
      return Promise.reject(new Error("Patient identity is not ready"));
    }
    if (this.failedEditIntent?.id === id) this.failedEditIntent = null;
    return this.manager.retryFailedWrite(id);
  }

  /**
   * Arm the visible "Edit section" recovery action. Only the next queued body
   * for the same clinical section and patient identity consumes the intent.
   */
  beginFailedWriteEdit(id: string): boolean {
    const failed = this.currentSnapshot.failed.find(
      (candidate) => candidate.id === id,
    );
    if (!failed?.sectionKey) return false;
    this.failedEditIntent = {
      id,
      sectionKey: failed.sectionKey,
      targetIdentity: this.managerIdentity ?? this.lastIdentity,
    };
    return true;
  }

  editFailedWrite(
    id: string,
    replacement: Record<string, unknown>,
  ): Promise<void> {
    if (!this.manager) {
      return Promise.reject(new Error("Patient identity is not ready"));
    }
    if (this.failedEditIntent?.id === id) this.failedEditIntent = null;
    return this.manager.editFailedWrite(id, replacement);
  }

  dismissFailedWrite(id: string): Promise<void> {
    if (!this.manager) {
      return Promise.reject(new Error("Patient identity is not ready"));
    }
    if (this.failedEditIntent?.id === id) this.failedEditIntent = null;
    return this.manager.dismissFailedWrite(id);
  }

  private async transferEligible(
    identity: string,
    manager: PatientSyncManager,
  ): Promise<void> {
    for (;;) {
      if (this.manager !== manager || this.managerIdentity !== identity) return;
      const index = this.staged.findIndex(
        (entry) =>
          entry.targetIdentity === null || entry.targetIdentity === identity,
      );
      if (index < 0) return;
      const entry = this.staged[index]!;

      // enqueue() captures synchronously before returning. Ownership therefore
      // transfers to the identity-scoped manager before the gate drops its copy,
      // even when AsyncStorage subsequently fails and retries.
      const durableAcceptance =
        entry.replaceFailedId === undefined
          ? manager.enqueue(entry.body)
          : manager.editFailedWrite(entry.replaceFailedId, entry.body);
      this.staged.splice(index, 1);
      this.publish();
      try {
        await durableAcceptance;
        if (
          entry.replaceFailedId !== undefined &&
          manager
            .cachedSnapshot()
            .failed.some((failed) => failed.id === entry.replaceFailedId)
        ) {
          const sectionKey = patientWriteSection(entry.body);
          if (sectionKey) {
            this.failedEditIntent = {
              id: entry.replaceFailedId,
              sectionKey,
              targetIdentity: identity,
            };
          }
        }
        entry.resolveAccepted();
      } catch {
        entry.targetIdentity = identity;
        this.staged.push(entry);
        this.publish();
        return;
      }
    }
  }

  private publish(): void {
    const managerSnapshot = this.manager?.cachedSnapshot();
    if (managerSnapshot) {
      this.currentSnapshot = {
        ...managerSnapshot,
        identityReady: true,
      };
    } else {
      const target = this.lastIdentity;
      const stagedForCurrentIdentity = this.staged.filter(
        (entry) => entry.targetIdentity === target,
      ).length;
      const stagedWritesForCurrentIdentity = this.staged.filter(
        (entry) => entry.targetIdentity === target,
      );
      this.currentSnapshot = {
        ...EMPTY_GATE_SNAPSHOT,
        hydrated: stagedForCurrentIdentity > 0,
        pendingSections: countPatientWriteSections(
          stagedWritesForCurrentIdentity,
        ),
        accepting: stagedForCurrentIdentity,
        undurable: stagedForCurrentIdentity,
      };
    }
    for (const listener of this.listeners) listener(this.currentSnapshot);
  }
}
